import React, { useState, useCallback, useMemo } from "react";
import { Card, Table, Modal, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {Link} from 'react-router-dom';
import { deleteUser } from "../../stateManagement/actions/auth";
import NewUserForm from "../forms/newUserForm";
import { StopOutlined, DeleteOutlined, UserSwitchOutlined, RollbackOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { ADMIN_ACCESS, ADMIN_ACCESS_MASK } from "../../utils/constants";
import { changePublisherView } from "../../stateManagement/actions/report";
import { useAuth } from "../../hooks/useAuth";


const UsersList = ({list, reloadData}) => {

    const {user} = useAuth();
    const dispatch = useDispatch();

      // Extrating report-related state
    const reportReducer = useSelector((state) => state.reportReducer);
     const {publisherView} = reportReducer;

    // Consolidated modal state management
    const [modalState, setModalState] = useState({
        newUser: { visible: false },
        deleteUser: { visible: false, id: null },
        switchPublisherView: { visible: false, access: publisherView }
    });

    // Memoized modal handlers to prevent unnecessary recreations
    const showNewUserModal = useCallback(() => {
        setModalState(prev => ({
            ...prev, 
            newUser: { visible: true }
        }));
    }, []);

    const handleNewUserCancel = useCallback(() => {
        setModalState(prev => ({
            ...prev, 
            newUser: { visible: false }
        }));
    }, []);

    const showDeleteUserModal = useCallback((id) => {
        setModalState(prev => ({
            ...prev, 
            deleteUser: { visible: true, id }
        }));
    }, []);

    const handleDeleteUserCancel = useCallback(() => {
        setModalState(prev => ({
            ...prev, 
            deleteUser: { visible: false, id: null }
        }));
    }, []);

    const showSwitchPublisherViewModal = useCallback((access) => {
        setModalState(prev => ({
            ...prev, 
            switchPublisherView: { visible: true, access }
        }));
    }, []);

    const handleSwitchPublisherViewCancel = useCallback(() => {
        setModalState(prev => ({
            ...prev, 
            switchPublisherView: { visible: false, access: ADMIN_ACCESS_MASK }
        }));
    }, []);

    // Memoized columns to prevent unnecessary recreations
    const usersColumns = useMemo(() => [
        {   
            key: 'email',
            title: 'Email',
            align: "right",
            dataIndex: 'email',
            sorter: (a,b) => a.email.localeCompare(b.email)
        },
        {
            key: 'access',
            title: 'Access',
            align: 'center',
            dataIndex: 'access',
        },
        {
            key: 'action_1',
            title: 'Delete',
            align: 'center',
            render: (data) => <DeleteOutlined onClick={() => showDeleteUserModal(data._id)} style={{ color: 'red', fontSize: '16px' }}/>
        },
        {
            key: 'action_2',
            title: 'View as',
            align: 'center',
            render: (data) => {

                const isAdmin = data.access === ADMIN_ACCESS;
                const isCurrentUser = data.email === user.email;
                const isAdminView = publisherView === ADMIN_ACCESS_MASK;
                const views = data.access.split('+');

                if (isAdmin && isCurrentUser && isAdminView) {
                    return (
                    <Tooltip title="Your account">
                        <CheckCircleOutlined style={{ color: '#0085ff', fontSize: 16 }} />
                    </Tooltip>
                    );
                }

                if (isAdmin && isCurrentUser && !isAdminView) {
                    return (
                    <RollbackOutlined
                        onClick={() => showSwitchPublisherViewModal(ADMIN_ACCESS_MASK)}
                        style={{ color: '#0085ff', fontSize: 16 }}
                    />
                    );
                }

                if (isAdmin && !isCurrentUser) {
                    return (
                    <Tooltip title="You can't switch to an admin account">
                        <StopOutlined style={{ color: 'orange', fontSize: 16 }} />
                    </Tooltip>
                    );
                }

                return (
                    views.map((view) => (
                        <Tooltip key={view} title={`View as ${view}`}>
                            <UserSwitchOutlined
                                onClick={() => showSwitchPublisherViewModal(view)}
                                style={{ color: 'green', fontSize: 16, margin: "auto 8px" }}
                            />
                        </Tooltip>)
                    )
                );
            }
        }
    ], [showDeleteUserModal, showSwitchPublisherViewModal, user.email, publisherView])

    // Memoized delete handler
    const handleDelete = useCallback(() => {
        dispatch(deleteUser(modalState.deleteUser.id));
        reloadData();
        handleDeleteUserCancel()
    }, [
        dispatch, 
        modalState.deleteUser.id, 
        reloadData, 
        handleDeleteUserCancel
    ]);

    // Memoized switch publisher view handler
    const handleSwitchPublisherView = useCallback(() => {
        dispatch(changePublisherView(modalState.switchPublisherView.access));
        reloadData();
        handleSwitchPublisherViewCancel()
    }, [
        dispatch, 
        modalState.switchPublisherView.access, 
        reloadData, 
        handleSwitchPublisherViewCancel
    ]);

    return(<>
        <Modal 
            title="Add new user" 
            open={modalState.newUser.visible} 
            onCancel={handleNewUserCancel}
            footer={null}
        >
            <NewUserForm reloadData={reloadData}/>
        </Modal>
        <Card size="small" extra={<Link onClick={showNewUserModal}>Create</Link>}>
            <Table 
                columns={usersColumns} 
                dataSource={list} 
                rowKey="_id"
                pagination={{
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `Total: ${total}`
                }}
            />            
        </Card>
        <Modal 
            title="Confirmation required (permanent action)" 
            open={modalState.deleteUser.visible} 
            onCancel={handleDeleteUserCancel}
            onOk={handleDelete}
        >
            <p>Are you sure you want to delete this user?</p>
        </Modal>
        <Modal 
            title="Confirmation required" 
            open={modalState.switchPublisherView.visible} 
            onCancel={handleSwitchPublisherViewCancel}
            onOk={handleSwitchPublisherView}
        >
            <p>You are about to change your account view mode. Are you sure?</p>
        </Modal>
    </>)
}

export default UsersList;